// deps
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";

// internal deps
import { Portal } from "@swiggy-private/dls-web-core/dist/components/Portal";
import { isEmpty } from "@swiggy-private/js-utils";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// constants
import { SCREEN_NAME } from "@cart-client/analytics/constants";
import { VALID_PL_BILL_DETAILS_TYPES } from "@swiggy-private/cart-ui";

// types
import { PLBillCtaAction, PLBillSubLineItem, PLBillTooltipLineItem } from "@swiggy-private/cart-types";
import { PL_BILL_TOOLTIP_ITEM_TEST_IDS } from "@swiggy-private/cart-test-ids";

// components
import { BillPrimitives } from "@swiggy-private/cart-ui";

// styles
import {
    InfoText,
    LeftColumn,
    LeftTriggerArea,
    RowInfoText,
    SubLineDescription,
    SubLineDescriptionTextSegment,
    SubLineTitleRow,
    SubLineRow,
    TooltipArrowAfter,
    TooltipArrowBefore,
    TooltipContainer,
    TooltipRow,
} from "./BillTooltipItem.style";
import { isTextualPrimitive } from "./helpers";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { ToolTipDetailsWrapper } from "./styles";

interface BillTooltipItemProps {
    item: PLBillTooltipLineItem;
    actionMap?: Record<PLBillCtaAction, VoidFunction>;
}

const TOOLTIP_WIDTH_MAP = { L: 180, XL: 230, XXL: 240, XXXL: 270 } as const;
const DEFAULT_TOOLTIP_WIDTH = TOOLTIP_WIDTH_MAP.XXL;

const TOOLTIP_DEFAULTS = {
    ARROW_LEFT_POSITION: 28,
    PADDING: 16,
    AFTER_LEFT_POSITION_OFFSET: 4,
};

const renderSubLineItem = (subItem: PLBillSubLineItem, idx: number) => {
    switch (subItem.type) {
        case VALID_PL_BILL_DETAILS_TYPES.TITLE:
            return (
                <SubLineTitleRow key={idx} data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.SUB_LINE_TITLE_ROW}>
                    {subItem?.details?.map((col, colIdx) => (
                        <BillPrimitives key={colIdx} items={col.items} />
                    ))}
                </SubLineTitleRow>
            );

        case VALID_PL_BILL_DETAILS_TYPES.DISPLAY:
            return (
                <React.Fragment key={idx}>
                    <SubLineRow data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.SUB_LINE_DISPLAY_ROW}>
                        {subItem?.details?.map((col, colIdx) => (
                            <BillPrimitives key={colIdx} items={col?.items} />
                        ))}
                    </SubLineRow>
                    {subItem?.details?.map(
                        (col, colIdx) =>
                            !isEmpty(col?.info_text) && (
                                <SubLineDescription key={colIdx}>
                                    {col?.info_text?.filter(isTextualPrimitive).map((txt, i) => (
                                        <SubLineDescriptionTextSegment key={i}>
                                            <Text
                                                as="span"
                                                variant={
                                                    txt.properties?.dls?.textVariant || "V2.overline.neutral.regular"
                                                }
                                                color={txt.properties?.dls?.textColor || "text_Low_Emphasis"}
                                                style={txt.properties?.style as React.CSSProperties}
                                            >
                                                {txt.text}
                                            </Text>
                                        </SubLineDescriptionTextSegment>
                                    ))}
                                </SubLineDescription>
                            ),
                    )}
                </React.Fragment>
            );

        default:
            return null;
    }
};

const BillTooltipItem = ({ item, actionMap }: BillTooltipItemProps) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const { clickEvent } = useContext(AnalyticsContext);

    const [leftCol, ...rightCols] = item.details;
    const hasTooltip = !isEmpty(leftCol.sub_line_items);
    const tooltipWidth = item.tooltip_width ? TOOLTIP_WIDTH_MAP[item.tooltip_width] : DEFAULT_TOOLTIP_WIDTH;

    const firstNonIconIdx = leftCol.items.findIndex((i) => i.type !== "icon");
    const leadingIcons = firstNonIconIdx > 0 ? leftCol.items.slice(0, firstNonIconIdx) : [];
    const labelItems = firstNonIconIdx >= 0 ? leftCol.items.slice(firstNonIconIdx) : leftCol.items;

    const getXOffset = useCallback(() => {
        if (typeof window !== "object") return 0;

        const { x } = position;
        if (x < TOOLTIP_DEFAULTS.PADDING * 2) return -TOOLTIP_DEFAULTS.PADDING;
        if (x + tooltipWidth + TOOLTIP_DEFAULTS.PADDING > window.innerWidth)
            return Math.abs(window.innerWidth - x - TOOLTIP_DEFAULTS.PADDING - tooltipWidth);

        return 0;
    }, [position, tooltipWidth]);

    const getContainerStyle = useCallback(() => {
        const { x, y } = position;
        const xOffset = getXOffset();
        const transform = `translate(${x - xOffset}px, ${y}px) translateY(-100%)`;
        return { transform, WebkitTransform: transform, width: `${tooltipWidth}px` };
    }, [position, tooltipWidth, getXOffset]);

    const getArrowStyle = useCallback(
        (offset = 0) => ({ left: `${TOOLTIP_DEFAULTS.ARROW_LEFT_POSITION + getXOffset() - offset}px` }),
        [getXOffset],
    );

    const hide = useCallback(() => setShowTooltip(false), []);

    const openTooltip = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!hasTooltip || !triggerRef.current) return;

            const { left, top, width } = triggerRef.current.getBoundingClientRect();
            setPosition({ x: left + width / 2, y: top });
            setShowTooltip(true);

            const beAnalytics = item.analytics;

            if (beAnalytics?.click_action) {
                clickEvent({
                    category: beAnalytics.screen_name ?? SCREEN_NAME.CHECKOUT,
                    action: beAnalytics.click_action,
                    context: beAnalytics.context,
                });
            }
        },
        [hasTooltip, item.analytics, clickEvent],
    );

    const getScrollParent = useCallback((element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;
        let parent = element.parentElement;
        while (parent) {
            const { overflow, overflowY } = window.getComputedStyle(parent);
            if (overflowY === "auto" || overflowY === "scroll" || overflow === "auto" || overflow === "scroll") {
                return parent;
            }
            parent = parent.parentElement;
        }
        return document.documentElement;
    }, []);

    useEffect(() => {
        if (!showTooltip) return;

        const onClick = (event: MouseEvent) => {
            if (
                event.target == null ||
                tooltipRef.current == null ||
                tooltipRef.current === event.target ||
                (event.target instanceof Node && tooltipRef.current.contains(event.target))
            )
                return;
            hide();
        };

        let timer = 0;
        const scrollParent = getScrollParent(triggerRef.current);
        const onScroll = () => {
            window.cancelAnimationFrame(timer);
            timer = window.requestAnimationFrame(hide);
        };

        document.addEventListener("click", onClick, false);
        scrollParent?.addEventListener("scroll", onScroll, false);

        return () => {
            window.cancelAnimationFrame(timer);
            document.removeEventListener("click", onClick, false);
            scrollParent?.removeEventListener("scroll", onScroll, false);
        };
    }, [showTooltip, hide, getScrollParent]);

    return (
        <>
            <TooltipRow>
                <LeftTriggerArea
                    ref={triggerRef}
                    onClick={openTooltip}
                    data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.TRIGGER_AREA}
                >
                    {leadingIcons.length > 0 && <BillPrimitives items={leadingIcons} />}
                    <LeftColumn>
                        <BillPrimitives items={labelItems} />
                    </LeftColumn>
                </LeftTriggerArea>
                {rightCols?.map((col, idx) => (
                    <BillPrimitives key={idx} items={col.items} />
                ))}
            </TooltipRow>

            {rightCols?.map(
                (col, idx) =>
                    !isEmpty(col.info_text) && (
                        <RowInfoText key={idx} data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.RIGHT_INFO_TEXT}>
                            <BillPrimitives items={col.info_text} actionMap={actionMap} />
                        </RowInfoText>
                    ),
            )}

            {!isEmpty(leftCol.info_text) && (
                <InfoText data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.LEFT_INFO_TEXT}>
                    {leftCol.info_text.filter(isTextualPrimitive).map((txt, i) => (
                        <Text
                            key={i}
                            variant={txt.properties?.dls?.textVariant || "V2.body3.neutral.regular"}
                            color={txt.properties?.dls?.textColor || "text_Low_Emphasis"}
                            style={txt.properties?.style as React.CSSProperties}
                        >
                            {txt.text}
                        </Text>
                    ))}
                </InfoText>
            )}

            {showTooltip && hasTooltip && !isEmpty(leftCol.sub_line_items) && (
                <Portal>
                    <TooltipContainer
                        ref={tooltipRef}
                        style={getContainerStyle()}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={PL_BILL_TOOLTIP_ITEM_TEST_IDS.TOOLTIP_CONTAINER}
                    >
                        <TooltipArrowBefore style={getArrowStyle()} />
                        {/* MRO carts can have multiple sub-line items, so the tooltip should be bounded and scrollable. */}
                        <ToolTipDetailsWrapper isMROFlow={isMROFlow()}>
                            {leftCol.sub_line_items.map((subItem, idx) => renderSubLineItem(subItem, idx))}
                        </ToolTipDetailsWrapper>
                        <TooltipArrowAfter style={getArrowStyle(TOOLTIP_DEFAULTS.AFTER_LEFT_POSITION_OFFSET)} />
                    </TooltipContainer>
                </Portal>
            )}
        </>
    );
};

export default memo(BillTooltipItem);
